import { useEffect } from 'react';

import { Stack } from '@mui/material';

import { ConfirmationModal } from 'src/components/Modals';
import { NPIDataGridRow } from 'src/components/NPIDataGrid/NPIDataGrid';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { SepaAgreementTable } from 'src/features/finance/sepaDirectDebits/components/SepaAgreementTables/SepaAgreementTable/SepaAgreementTable';
import useSepaAgreement from 'src/features/finance/sepaDirectDebits/hooks/useSepaAgreement';

export type SepaAgreementTablesProps = {
    shouldRefetch: number;
};

export function SepaAgreementTables({ shouldRefetch }: SepaAgreementTablesProps) {
    const {
        merchant,
        partner,
        pdfFile,
        adobeDocumentPdf,
        isPDFModalOpen,
        setIsPDFModalOpen,
        loadingAgreementDocumentPdf,
        isDeleteConfirmModalOpen,
        setIsDeleteConfirmModalOpen,
        handleDeleteSepaMandate,
        handleRemindRecord,
        isRemindConfirmModalOpen,
        setIsRemindConfirmModalOpen,
        remindData,
    } = useSepaAgreement();

    useEffect(() => {
        if (shouldRefetch > 0) {
            merchant?.handleRefetchMerchantData();
            partner?.handleRefetchPartnerData();
        }
        // only want this to run if shouldRefetch updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRefetch]);

    return (
        <>
            <Stack>
                <SepaAgreementTable
                    key={'merchant-sepa-agreements'}
                    title="Merchant Direct Debit Mandates"
                    tableRowData={(merchant?.merchantData as NPIDataGridRow[]) ?? []}
                    dataTestId="merchant-dd-agreements-table"
                    loadingAgreementDocumentPdf={loadingAgreementDocumentPdf}
                    loadingTableRowData={merchant?.loadingMerchantData}
                    tableColDefs={merchant?.merchantSepaAgreementColumns}
                    handleReset={merchant?.handleRefetchMerchantData}
                    onFilterChange={merchant?.updateFilters}
                    filterState={merchant?.filterState}
                    serverSideSorting={{
                        onSortModelChange: merchant?.updateSorting,
                        sortModel: merchant?.sortState,
                    }}
                    serverSidePagination={{
                        mode: 'server',
                        page: merchant?.paginationMetadata?.page
                            ? merchant.paginationMetadata.page - 1
                            : 1,
                        pageSize: merchant?.paginationMetadata?.pageSize,
                        totalRows: merchant?.paginationMetadata?.total ?? 0,
                        onPageChange: (page, pageSize) => {
                            merchant?.fetchMore({ page: page + 1, pageSize });
                        },
                    }}
                />

                <SepaAgreementTable
                    key={'partner-sepa-agreements'}
                    title="Partner Direct Debit Mandates"
                    tableRowData={(partner?.partnerData as NPIDataGridRow[]) ?? []}
                    dataTestId="partner-dd-agreements-table"
                    handleReset={partner?.handleRefetchPartnerData}
                    loadingAgreementDocumentPdf={loadingAgreementDocumentPdf}
                    loadingTableRowData={partner?.loadingPartnerData}
                    tableColDefs={partner?.partnerSepaAgreementColumns}
                    onFilterChange={partner?.updateFilters}
                    filterState={partner?.filterState}
                    serverSideSorting={{
                        onSortModelChange: partner?.updateSorting,
                        sortModel: partner?.sortState,
                    }}
                    serverSidePagination={{
                        mode: 'server',
                        page: partner?.paginationMetadata?.page
                            ? partner.paginationMetadata.page - 1
                            : 0,
                        pageSize: partner?.paginationMetadata?.pageSize,
                        totalRows: partner?.paginationMetadata?.total ?? 0,
                        onPageChange: (page, pageSize) => {
                            partner?.fetchMore({ page: page + 1, pageSize });
                        },
                    }}
                />
            </Stack>
            <ViewPDFModal
                title="View Direct Debit Mandate PDF"
                file={pdfFile}
                fileURL={adobeDocumentPdf?.downloadLink}
                isModalOpen={isPDFModalOpen}
                setIsModalOpen={setIsPDFModalOpen}
            />
            <ConfirmationModal
                isOpen={isDeleteConfirmModalOpen}
                onCancel={() => {
                    setIsDeleteConfirmModalOpen(false);
                }}
                message="Are you sure you want to delete the mandate?"
                title="Delete Direct Debit Mandate"
                onConfirm={handleDeleteSepaMandate}
            />
            <ConfirmationModal
                title="Send Reminder re. Direct Debit Mandate?"
                message={`${remindData?.contactName} - (${remindData?.sentToEmail})`}
                isOpen={isRemindConfirmModalOpen}
                onCancel={() => {
                    setIsRemindConfirmModalOpen(false);
                }}
                onConfirm={handleRemindRecord}
            />
        </>
    );
}

export default SepaAgreementTables;
